import { ACTIVE_TEMPLATE_STORAGE_KEY, SESSION_DIRTY_KEY } from '@/editor/utils/editorSession';

export type AuthRedirectReason = 'expired' | 'logout' | 'unauthenticated';

interface StoredAuthRedirect {
  path: string;
  reason: AuthRedirectReason;
  savedAt: number;
}

export interface StoredEditorAuthResume {
  hadDirtyState: boolean;
  path: string;
  savedAt: number;
  templateId: string | null;
}

const DEFAULT_REDIRECT_PATH = '/dashboard';
const AUTH_REDIRECT_STORAGE_KEY = 'snaplayer.auth.redirect.v1';
const EDITOR_AUTH_RESUME_STORAGE_KEY = 'snaplayer.auth.editor-resume.v1';
const EXPLICIT_SIGN_OUT_STORAGE_KEY = 'snaplayer.auth.explicit-sign-out.v1';
const STORED_REDIRECT_MAX_AGE_MS = 60 * 60 * 1000;
const STORED_EDITOR_RESUME_MAX_AGE_MS = 20 * 60 * 1000;
const EXPLICIT_SIGN_OUT_MAX_AGE_MS = 15 * 1000;

const PUBLIC_PATH_PREFIXES = [
  '/activation',
  '/confirm-',
  '/d/',
  '/doc/',
  '/docs',
  '/magic-link-expired',
  '/r/',
  '/reporting',
  '/reset-password',
  '/retrieve-codes',
  '/roadmap',
  '/sign/',
  '/snaplinks-test',
  '/ui-audit',
];

const PUBLIC_PATHS = new Set([
  '/',
  '/activate',
  '/login',
  '/signup',
]);

const isAuthRedirectReason = (value: unknown): value is AuthRedirectReason => {
  return value === 'expired' || value === 'logout' || value === 'unauthenticated';
};

const getWindowOrigin = (): string => {
  if (typeof window !== 'undefined' && typeof window.location?.origin === 'string' && window.location.origin.length > 0) {
    return window.location.origin;
  }
  return 'http://localhost';
};

const getLocalStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const getSessionStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const readStoredJson = <T,>(storage: Storage | null, key: string): T | null => {
  if (!storage) {
    return null;
  }
  try {
    const raw = storage.getItem(key);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const isFreshTimestamp = (value: unknown, maxAgeMs: number): value is number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return false;
  }
  return Date.now() - value <= maxAgeMs;
};

const readActiveTemplateId = (): string | null => {
  const storage = getLocalStorage();
  if (!storage) {
    return null;
  }
  try {
    const activeTemplateId = storage.getItem(ACTIVE_TEMPLATE_STORAGE_KEY);
    if (typeof activeTemplateId === 'string' && activeTemplateId.trim().length > 0) {
      return activeTemplateId.trim();
    }
  } catch {
    return null;
  }
  return null;
};

const normalizePathname = (rawPath: string): string => {
  if (rawPath.length <= 1) {
    return rawPath;
  }
  return rawPath.replace(/\/+$/, '');
};

export const sanitizeAppRelativePath = (rawPath: string | null | undefined): string | null => {
  if (typeof rawPath !== 'string') {
    return null;
  }
  const trimmed = rawPath.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return null;
  }

  try {
    const parsed = new URL(trimmed, getWindowOrigin());
    if (parsed.origin !== getWindowOrigin()) {
      return null;
    }
    const pathname = normalizePathname(parsed.pathname || '/');
    return `${pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
};

export const extractEditorTemplateId = (rawPath: string | null | undefined): string | null => {
  const normalized = sanitizeAppRelativePath(rawPath);
  if (!normalized) {
    return null;
  }

  try {
    const parsed = new URL(normalized, getWindowOrigin());
    const segments = parsed.pathname.split('/').filter(Boolean);
    if (segments[0] !== 'editor' || typeof segments[1] !== 'string' || segments[1].trim().length === 0) {
      return null;
    }
    return decodeURIComponent(segments[1]);
  } catch {
    return null;
  }
};

export const normalizeAuthRedirectPath = (rawPath: string | null | undefined): string | null => {
  const normalized = sanitizeAppRelativePath(rawPath);
  if (!normalized) {
    return null;
  }

  try {
    const parsed = new URL(normalized, getWindowOrigin());
    const pathname = normalizePathname(parsed.pathname || '/');
    if (pathname === '/editor') {
      const activeTemplateId = readActiveTemplateId();
      if (activeTemplateId) {
        parsed.pathname = `/editor/${encodeURIComponent(activeTemplateId)}`;
      }
    }
    const resolvedPath = normalizePathname(parsed.pathname || '/');
    return `${resolvedPath}${parsed.search}${parsed.hash}`;
  } catch {
    return normalized;
  }
};

export const getCurrentAppPath = (): string => {
  if (typeof window === 'undefined') {
    return DEFAULT_REDIRECT_PATH;
  }
  return normalizeAuthRedirectPath(`${window.location.pathname}${window.location.search}${window.location.hash}`) ?? DEFAULT_REDIRECT_PATH;
};

export const isPublicAppPath = (rawPath: string | null | undefined): boolean => {
  const normalized = sanitizeAppRelativePath(rawPath ?? getCurrentAppPath());
  if (!normalized) {
    return false;
  }

  try {
    const pathname = normalizePathname(new URL(normalized, getWindowOrigin()).pathname || '/');
    if (PUBLIC_PATHS.has(pathname)) {
      return true;
    }
    return PUBLIC_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix));
  } catch {
    return false;
  }
};

export const storeAuthRedirect = (path: string, reason: AuthRedirectReason): string | null => {
  const storage = getSessionStorage();
  const normalized = normalizeAuthRedirectPath(path);
  if (!storage || !normalized) {
    return normalized;
  }

  try {
    const payload: StoredAuthRedirect = {
      path: normalized,
      reason,
      savedAt: Date.now(),
    };
    storage.setItem(AUTH_REDIRECT_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    return normalized;
  }

  return normalized;
};

export const readStoredAuthRedirect = (): StoredAuthRedirect | null => {
  const storage = getSessionStorage();
  const parsed = readStoredJson<StoredAuthRedirect>(storage, AUTH_REDIRECT_STORAGE_KEY);
  if (!parsed || !isFreshTimestamp(parsed.savedAt, STORED_REDIRECT_MAX_AGE_MS) || !isAuthRedirectReason(parsed.reason)) {
    clearStoredAuthRedirect();
    return null;
  }

  const normalizedPath = normalizeAuthRedirectPath(parsed.path);
  if (!normalizedPath) {
    clearStoredAuthRedirect();
    return null;
  }

  return {
    path: normalizedPath,
    reason: parsed.reason,
    savedAt: parsed.savedAt,
  };
};

export const clearStoredAuthRedirect = (): void => {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }
  try {
    storage.removeItem(AUTH_REDIRECT_STORAGE_KEY);
  } catch {
    // Ignore storage cleanup errors.
  }
};

const readEditorDirtyState = (): boolean => {
  const storage = getLocalStorage();
  if (!storage) {
    return false;
  }
  try {
    return storage.getItem(SESSION_DIRTY_KEY) === '1';
  } catch {
    return false;
  }
};

export const storeEditorAuthResume = (path: string): StoredEditorAuthResume | null => {
  const storage = getSessionStorage();
  const normalized = normalizeAuthRedirectPath(path);
  if (!storage || !normalized || !normalizePathname(new URL(normalized, getWindowOrigin()).pathname || '/').startsWith('/editor')) {
    return null;
  }

  const payload: StoredEditorAuthResume = {
    hadDirtyState: readEditorDirtyState(),
    path: normalized,
    savedAt: Date.now(),
    templateId: extractEditorTemplateId(normalized),
  };

  try {
    storage.setItem(EDITOR_AUTH_RESUME_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    return payload;
  }

  return payload;
};

export const readEditorAuthResume = (): StoredEditorAuthResume | null => {
  const storage = getSessionStorage();
  const parsed = readStoredJson<StoredEditorAuthResume>(storage, EDITOR_AUTH_RESUME_STORAGE_KEY);
  if (!parsed || !isFreshTimestamp(parsed.savedAt, STORED_EDITOR_RESUME_MAX_AGE_MS)) {
    clearEditorAuthResume();
    return null;
  }

  const normalizedPath = normalizeAuthRedirectPath(parsed.path);
  if (!normalizedPath) {
    clearEditorAuthResume();
    return null;
  }

  return {
    hadDirtyState: parsed.hadDirtyState === true,
    path: normalizedPath,
    savedAt: parsed.savedAt,
    templateId: typeof parsed.templateId === 'string' && parsed.templateId.trim().length > 0 ? parsed.templateId.trim() : null,
  };
};

export const clearEditorAuthResume = (): void => {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }
  try {
    storage.removeItem(EDITOR_AUTH_RESUME_STORAGE_KEY);
  } catch {
    // Ignore storage cleanup errors.
  }
};

export const prepareAuthRedirect = (
  reason: AuthRedirectReason,
  options?: {
    fromPath?: string | null;
    preservePath?: boolean;
  },
): string | null => {
  const preservePath = options?.preservePath ?? reason !== 'logout';
  if (!preservePath) {
    return null;
  }

  const currentPath = normalizeAuthRedirectPath(options?.fromPath ?? getCurrentAppPath());
  if (!currentPath || isPublicAppPath(currentPath)) {
    return currentPath;
  }

  const storedPath = storeAuthRedirect(currentPath, reason);
  if (reason === 'expired' && storedPath?.startsWith('/editor')) {
    storeEditorAuthResume(storedPath);
  }
  return storedPath;
};

export const redirectToLogin = (
  reason: AuthRedirectReason,
  options?: {
    fromPath?: string | null;
    preservePath?: boolean;
  },
): void => {
  prepareAuthRedirect(reason, options);
  if (typeof window !== 'undefined') {
    window.location.replace('/login');
  }
};

export const markExplicitSignOut = (): void => {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }
  try {
    storage.setItem(EXPLICIT_SIGN_OUT_STORAGE_KEY, String(Date.now()));
  } catch {
    // Ignore storage cleanup errors.
  }
};

export const hasRecentExplicitSignOut = (): boolean => {
  const storage = getLocalStorage();
  if (!storage) {
    return false;
  }
  try {
    const raw = storage.getItem(EXPLICIT_SIGN_OUT_STORAGE_KEY);
    if (!raw) {
      return false;
    }
    const savedAt = Number(raw);
    if (!isFreshTimestamp(savedAt, EXPLICIT_SIGN_OUT_MAX_AGE_MS)) {
      storage.removeItem(EXPLICIT_SIGN_OUT_STORAGE_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

export const clearExplicitSignOut = (): void => {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }
  try {
    storage.removeItem(EXPLICIT_SIGN_OUT_STORAGE_KEY);
  } catch {
    // Ignore storage cleanup errors.
  }
};

export const resolvePostLoginRedirect = (state: { from?: string; reason?: unknown } | null | undefined): {
  path: string;
  reason: AuthRedirectReason | null;
} => {
  const statePath = normalizeAuthRedirectPath(state?.from);
  const stateReason = isAuthRedirectReason(state?.reason) ? state.reason : null;
  const stored = readStoredAuthRedirect();

  return {
    path: statePath ?? stored?.path ?? DEFAULT_REDIRECT_PATH,
    reason: stateReason ?? stored?.reason ?? null,
  };
};